import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Type } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { urlValidator } from '@vendasta/forms';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import {
  KnowledgeSourceConfig,
  KnowledgeSourceConfigWebsiteScrapeConfig,
  WebsiteScrapeConfigCrawlMode,
  WebsiteScrapeConfigProgress,
} from '@vendasta/embeddings';
import { AiKnowledgeService } from '../../ai-knowledge.service';
import { MatIconModule } from '@angular/material/icon';
import { AiKnowledgeI18nModule } from '../../assets/i18n/ai-knowledge-i18n.module';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { ScrapedPagesComponent } from './scraped-pages/scraped-pages.component';
import { CommonKnowledgeSourceEditorComponent } from '../common/common-knowledge-source-editor.component';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatInputModule } from '@angular/material/input';
import { defer, from, map, retry, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Environment, EnvironmentService } from '@galaxy/core';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';

@Component({
  selector: 'ai-knowledge-website-scrape',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    MatIconModule,
    MatSelectModule,
    MatOptionModule,
    ReactiveFormsModule,
    FormsModule,
    GalaxyFormFieldModule,
    GalaxySnackbarModule,
    GalaxyLoadingSpinnerModule,
    MatInputModule,
  ],
  providers: [AiKnowledgeService],
  templateUrl: './website-scrape.component.html',
  styleUrls: ['./website-scrape.component.scss'],
})
export class WebsiteScrapeComponent extends CommonKnowledgeSourceEditorComponent {
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly snackbarService = inject(SnackbarService);
  private destroyRef = inject(DestroyRef);
  private environmentService = inject(EnvironmentService);
  websiteScrapeConfig: KnowledgeSourceConfigWebsiteScrapeConfig;
  form: FormGroup | undefined;
  crawlModes = WebsiteScrapeConfigCrawlMode;
  scrapeProgressStates = WebsiteScrapeConfigProgress;

  init(): void {
    this.websiteScrapeConfig = this.knowledgeSource?.config?.websiteScrapeConfig;
    this.form = new FormGroup({
      name: new FormControl(this.knowledgeSource?.name || '', [Validators.required]),
      url: new FormControl(this.websiteScrapeConfig?.url || '', [urlValidator, Validators.required]),
      crawlMode: new FormControl(
        this.websiteScrapeConfig?.crawlMode || WebsiteScrapeConfigCrawlMode.WEBSITE_SCRAPE_CONFIG_CRAWL_MODE_SINGLE,
      ),
    });
    if (this.knowledgeSource) {
      defer(() =>
        from(this.knowledgeService.getKnowledgeSource(this.knowledgeSource?.id, this.knowledgeSource?.namespace)),
      )
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map((ks) => {
            this.websiteScrapeConfig = ks?.config?.websiteScrapeConfig;
            if (
              this.websiteScrapeConfig?.scrapeProgress !==
              WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_DONE
            ) {
              // throw error to poll again
              throw false;
            }
          }),
          retry({ delay: 5000 }),
          take(1),
        )
        .subscribe();
    }
  }

  getCrawlModeTranslationKey(crawlMode: WebsiteScrapeConfigCrawlMode): string {
    switch (crawlMode) {
      case WebsiteScrapeConfigCrawlMode.WEBSITE_SCRAPE_CONFIG_CRAWL_MODE_SINGLE: {
        return 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.SINGLE_PAGE';
      }
      case WebsiteScrapeConfigCrawlMode.WEBSITE_SCRAPE_CONFIG_CRAWL_MODE_RECURSIVE: {
        return 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.FOLLOW_LINKS';
      }
      default: {
        return '';
      }
    }
  }

  getScrapeProgressTitle(): string {
    if (
      this.websiteScrapeConfig?.scrapeProgress === WebsiteScrapeConfigProgress.WEBSITE_SCRAPE_CONFIG_PROGRESS_QUEUED
    ) {
      return 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.SCRAPE_QUEUED';
    }
    return 'EDIT_KNOWLEDGE.WEBSITE_SCRAPE_CONFIG.SCRAPE_IN_PROGRESS';
  }

  getScrapedPagesCount(): number {
    return this.websiteScrapeConfig?.scrapedPages?.length || 0;
  }

  getTrainedPagesCount(): number {
    return this.websiteScrapeConfig?.scrapedPages?.filter((p) => p.trainingEnabled)?.length || 0;
  }

  getPageLimit(): number {
    switch (this.environmentService.getEnvironment()) {
      case Environment.LOCAL:
      case Environment.DEMO:
        return 10;
      case Environment.PROD:
        return 100;
      default:
        return 0;
    }
  }

  isFormValid(): boolean {
    const controls = [this.form.get('name'), this.form.get('url')];
    let valid = true;
    controls.forEach((c) => {
      c?.markAsDirty();
      if (!c?.valid) {
        valid = false;
      }
    });
    return valid;
  }

  async submit(): Promise<string> {
    if (!this.isFormValid()) {
      this.snackbarService.openErrorSnack('COMMON.INVALID_FORM');
      return;
    }
    const name = this.form.get('name').value;
    const url = this.form.get('url')?.value;
    const config = {
      websiteScrapeConfig: {
        url: url,
        crawlMode: this.form.get('crawlMode')?.value,
        scrapedPages: this.websiteScrapeConfig?.scrapedPages,
      },
    } as KnowledgeSourceConfig;
    if (this.knowledgeSource) {
      const source = this.knowledgeSource;
      source.name = name;
      source.config = config;
      await this.knowledgeService.updateKnowledgeSourceNameAndConfig(source);
      return source.id;
    } else {
      return await this.knowledgeService.createKnowledgeSource(name, config);
    }
  }

  nextStep(): Type<CommonKnowledgeSourceEditorComponent> {
    return ScrapedPagesComponent;
  }

  hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  getLastScraped(): string {
    return this.knowledgeService.getLastScraped(this.knowledgeSource);
  }
}
